import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, InfoIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { articles, getArticleBySlug, getArticlesByCluster } from '../data/blogArticles';
import { buildArticleSchema, buildBreadcrumbList } from '../lib/structuredData';
export const SEOBlogPage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = getArticleBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <Navigate to="/seo" replace data-id="element-2116" data-mp="qwntvekn8pnw" />;
  }
  const relatedClusterArticles = getArticlesByCluster(article.cluster).filter(a => a.slug !== article.slug).slice(0, 3);
  const renderContentBlock = (block: any, index: number) => {
    switch (block.type) {
      case 'heading':
        return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-6" data-id="element-2117" data-mp="17op54xrmtbu">
            {block.content}
          </h2>;
      case 'heading3':
        return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-8 mb-4" data-id="element-2118" data-mp="3odpc114txxd8">
            {block.content}
          </h3>;
      case 'paragraph':
        return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-2119" data-mp="3temtfbi0mndo">
            {block.content}
          </p>;
      case 'list':
        return <ul key={index} className="space-y-3 mb-8 ml-6 list-disc text-lg text-[#4B5563]" data-id="element-2120" data-mp="oykbhmjgcwbp">
            {block.items?.map((item: string, i: number) => <li key={i} className="pl-2 leading-relaxed" data-id="element-2121" data-mp="b6nn9tkwn6kl">
                {item}
              </li>)}
          </ul>;
      case 'callout':
        return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#007AFF] p-6 rounded-r-lg my-8" data-id="element-2122" data-mp="13v1ki7465yv3">
            <div className="flex items-start gap-4" data-id="element-2123" data-mp="18mtfctucyqr1">
              <InfoIcon className="w-6 h-6 text-[#007AFF] flex-shrink-0 mt-1" data-id="element-2124" data-mp="18oe7kct79xlq" />
              <p className="text-lg text-[#1B2B44] font-medium leading-relaxed m-0" data-id="element-2125" data-mp="1ihrwc2fir9ux">
                {block.content}
              </p>
            </div>
          </div>;
      case 'formula':
        return <div key={index} className="bg-[#1B2B44] text-white p-8 rounded-xl my-8 text-center shadow-lg" data-id="element-2126" data-mp="18iszri8134oa">
            <span className="text-sm text-white/60 uppercase tracking-wider font-semibold mb-3 block" data-id="element-2127" data-mp="13khkgryys1yo">
              De Formule
            </span>
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-2128" data-mp="usz6mnphpy8r">
              {block.content}
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full min-h-screen bg-white font-sans" data-id="element-2129" data-mp="pr9sv0avv6gz">
      <SEO title={`${article.title} | Noord Digital SEO Kennisbank`} description={article.metaDescription} canonical={`https://noorddigital.nl/seo/blog/${article.slug}`} keywords={`${article.title.toLowerCase()}, ${article.cluster.toLowerCase()}, seo uitleg, online vindbaarheid`} ogType="article" jsonLd={[buildArticleSchema({
      headline: article.title,
      description: article.metaDescription,
      path: `/seo/blog/${article.slug}`,
      section: article.cluster
    }), buildBreadcrumbList([{
      name: 'SEO',
      path: '/seo'
    }, {
      name: article.cluster,
      path: '/seo'
    }, {
      name: article.title,
      path: `/seo/blog/${article.slug}`
    }])]} data-id="element-2130" data-mp="kw2wyenuv62m" />
      <Header data-id="element-2131" data-mp="23uiovfcsuj9h" />

      {/* Breadcrumb & Hero */}
      <div className="bg-[#F7F8FA] pt-24 pb-16 border-b border-gray-200" data-id="element-2132" data-mp="28safm61pjzc3">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2133" data-mp="28sk6dvukuaiu">
          <nav className="flex items-center text-sm text-[#6B7280] mb-8" data-id="element-2134" data-mp="2dqlskpnaupyj">
            <Link to="/" className="hover:text-[#007AFF] transition-colors" data-id="element-2135" data-mp="2iidgstizgjli">
              Home
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2136" data-mp="1l1wxb78xdeup" />
            <Link to="/seo" className="hover:text-[#007AFF] transition-colors" data-id="element-2137" data-mp="1q1syyzu5gu73">
              SEO
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2138" data-mp="3rqgt8geyytt3" />
            <span className="text-[#111827] font-medium truncate" data-id="element-2139" data-mp="3rovz2rwkhcvr">
              {article.title}
            </span>
          </nav>

          <div className="max-w-3xl" data-id="element-2140" data-mp="5b46jebe0h2d">
            <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-semibold rounded-full mb-6" data-id="element-2141" data-mp="5c5rqbm4ajxt">
              {article.cluster}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] leading-tight mb-6" data-id="element-2142" data-mp="f8sbsy6md6ys">
              {article.title}
            </h1>
            <p className="text-xl text-[#6B7280] leading-relaxed" data-id="element-2143" data-mp="aa7b6xnggl9t">
              {article.metaDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content & Sidebar */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-16" data-id="element-2144" data-mp="jbydrtmp8bzg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-2145" data-mp="2q2efxgyenlsx">
          {/* Article Content */}
          <div className="lg:col-span-8" data-id="element-2146" data-mp="2l432yxctcv6h">
            <article className="prose prose-lg max-w-none prose-headings:text-[#111827] prose-p:text-[#4B5563] prose-a:text-[#007AFF]" data-id="element-2147" data-mp="10khfn3tynvd5">
              {article.content.map((block, index) => renderContentBlock(block, index))}
            </article>

            {/* Author / Bio block */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex items-center gap-6" data-id="element-2148" data-mp="rqcun11ide5j">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full bg-[#F7F8FA] border border-gray-200" data-id="element-2149" data-mp="vroaieukyl0j" />
              <div data-id="element-2150" data-mp="mqzyfpoqyuqa">
                <h4 className="font-bold text-[#111827]" data-id="element-2151" data-mp="8tbeql6f1tmt">Norden</h4>
                <p className="text-[#6B7280] text-sm" data-id="element-2152" data-mp="8v4jtw1ds5sp">
                  SEO Gids bij Noord Digital
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-2153" data-mp="23844i3kmbq6u">
            <div className="sticky top-24 space-y-8" data-id="element-2154" data-mp="278o6bkg4zhzb">
              {/* Back to SEO */}
              <Link to="/seo" className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-[#F7F8FA] hover:bg-gray-100 text-[#1B2B44] font-semibold rounded-xl transition-colors border border-gray-200" data-id="element-2155" data-mp="3g5uuqce6ksrq">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-2156" data-mp="374odg13pjdxf" />
                Terug naar SEO overzicht
              </Link>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] rounded-2xl p-8 text-white shadow-xl" data-id="element-2157" data-mp="327oba19zavbl">
                <h3 className="text-xl font-bold mb-3" data-id="element-2158" data-mp="2t2mflnnrmfn">
                  SEO als systeem inzetten?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6" data-id="element-2159" data-mp="3q429qpowt0r3">
                  Stop met losse flodders. Ontdek waar jouw kansen liggen voor
                  structurele groei in Google.
                </p>
                <a href="/prijsscan" className="block w-full py-3 px-4 bg-white text-[#007AFF] text-center font-bold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2160" data-mp="mf7e7nqihbjm">
                  Start de SEO scan
                </a>
              </div>

              {/* Related Articles */}
              {article.relatedArticles && article.relatedArticles.length > 0 && <div className="bg-white border border-gray-200 rounded-2xl p-6" data-id="element-2161" data-mp="tyqf58zpnpgh">
                    <h3 className="text-lg font-bold text-[#111827] mb-4 flex items-center gap-2" data-id="element-2162" data-mp="yw028hzmnp15">
                      <TargetIcon className="w-5 h-5 text-[#007AFF]" data-id="element-2163" data-mp="130pw61aq6tjf" />
                      Gerelateerd
                    </h3>
                    <ul className="space-y-3" data-id="element-2164" data-mp="17wez8elyeskh">
                      {article.relatedArticles.map((relSlug, idx) => {
                  const relArticle = getArticleBySlug(relSlug);
                  if (!relArticle) return null;
                  return <li key={idx} data-id="element-2165" data-mp="1mnno2li189wk" data-mpi={idx}>
                            <Link to={`/seo/blog/${relArticle.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug" data-id="element-2166" data-mp="1mmch6ctaim18">
                              <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" data-id="element-2167" data-mp="1volrb1kncde3" />
                              {relArticle.title}
                            </Link>
                          </li>;
                })}
                    </ul>
                  </div>}
            </div>
          </div>
        </div>
      </div>

      {/* More from this cluster */}
      {relatedClusterArticles.length > 0 && <div className="bg-[#F7F8FA] py-20 border-t border-gray-200" data-id="element-2168" data-mp="2lyeqlt1pygar">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2169" data-mp="1y7ht3lao7lj1">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-10 text-center" data-id="element-2170" data-mp="1t9q00nyahtd9">
              Meer over {article.cluster}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2171" data-mp="27zf4ot5lcfia">
              {relatedClusterArticles.map((relArticle, idx) => <Link key={idx} to={`/seo/blog/${relArticle.slug}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#007AFF]/30 transition-all group" data-id="element-2172" data-mp="235j91xcsv4qa" data-mpi={idx}>
                  <h3 className="text-lg font-bold text-[#1B2B44] mb-3 group-hover:text-[#007AFF] transition-colors" data-id="element-2173" data-mp="1agm22qe1gucu">
                    {relArticle.title}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed mb-4 line-clamp-3" data-id="element-2174" data-mp="3qycz387sj35i">
                    {relArticle.metaDescription}
                  </p>
                  <span className="text-[#007AFF] text-sm font-semibold flex items-center gap-1" data-id="element-2175" data-mp="3h679u68xazu0">
                    Lees artikel <ArrowRightIcon className="w-4 h-4" data-id="element-2176" data-mp="8s715a0xyemj" />
                  </span>
                </Link>)}
            </div>
          </div>
        </div>}

      <Footer data-id="element-2177" data-mp="dnw5rrrtilpp" />
    </div>;
};